import React from "react";
import { Typography } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis
} from "recharts";

import {
  ChartTooltipBox, EmptyState, Panel, StatCard, axisProps, chartInk, dateLabel, numberLabel, seriesColor
} from "./shared";

const useStyles = makeStyles(theme => ({
  grid: { display: "grid", gap: theme.spacing(2), gridTemplateColumns: "minmax(0, 1fr)" },
  cards: { display: "grid", gap: theme.spacing(1.5), gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" },
  legend: { display: "flex", flexWrap: "wrap", gap: theme.spacing(0.5, 2), fontSize: 12, color: theme.palette.text.secondary, marginBottom: theme.spacing(1) },
  legendItem: { display: "inline-flex", alignItems: "center", gap: 6 }
}));

const percentLabel = value => (value === null || value === undefined ? "-" : `${numberLabel(value)}%`);

const TicketsAfterDelivery = ({ rows, base }) => {
  const classes = useStyles();
  const theme = useTheme();
  const ink = chartInk(theme);
  if (!rows.length) return <EmptyState>Nenhum projeto entregue no período.</EmptyState>;
  const color = seriesColor(theme, 0);
  const data = rows.map(row => ({ ...row, label: `#${row.projeto_id}` }));

  return (
    <>
      <div className={classes.legend}>
        <span className={classes.legendItem}><span style={{ width: 10, height: 10, borderRadius: 2, background: color }} />Janela de 60 dias completa</span>
        <span className={classes.legendItem}><span style={{ width: 10, height: 10, borderRadius: 2, background: color, opacity: 0.4 }} />Janela ainda em andamento</span>
        {base.media !== null && (
          <span className={classes.legendItem}><span style={{ width: 18, height: 2, background: ink.secondary }} />Média da base: {numberLabel(base.media, 2)}</span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} margin={{ top: 16, right: 16, bottom: 4, left: -12 }} barCategoryGap="25%">
          <CartesianGrid vertical={false} stroke={ink.grid} />
          <XAxis dataKey="label" minTickGap={8} {...axisProps(theme)} />
          <YAxis allowDecimals={false} {...axisProps(theme)} />
          <ChartTooltip cursor={{ fill: theme.palette.action.hover }} content={({ active, payload }) => (active && payload?.length ? (
            <ChartTooltipBox title={`#${payload[0].payload.projeto_id} · ${payload[0].payload.cliente}`} rows={[
              { label: "Chamados em 60 dias", value: payload[0].payload.chamados, color },
              { label: "Entrega", value: dateLabel(payload[0].payload.entrega_em) },
              { label: "Média da base", value: numberLabel(base.media, 2) },
              { label: "Janela", value: payload[0].payload.janela_completa ? "completa" : "em andamento" }
            ]} />
          ) : null)} />
          <Bar dataKey="chamados" name="Chamados pós-entrega" radius={[4, 4, 0, 0]} maxBarSize={48}>
            {data.map(row => <Cell key={row.projeto_id} fill={color} fillOpacity={row.janela_completa ? 1 : 0.4} />)}
          </Bar>
          {base.media !== null && (
            <ReferenceLine y={base.media} stroke={ink.secondary} strokeWidth={2}
              label={{ value: "Média da base", position: "insideTopRight", fill: ink.secondary, fontSize: 11 }} />
          )}
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

const ResultsTab = ({ data }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { results } = data;
  const base = results.base_average;

  return (
    <div className={classes.grid}>
      <div className={classes.cards}>
        <StatCard label="Projetos entregues no prazo" value={percentLabel(results.on_time.percentual)}
          detail={`${results.on_time.no_prazo} de ${results.on_time.total} com previsão${results.on_time.sem_previsao ? ` · ${results.on_time.sem_previsao} sem previsão` : ""}`}
          accent={seriesColor(theme, 0)} />
        <StatCard label="Revisões de 90 dias feitas" value={percentLabel(results.reviews.percentual)}
          detail={`${results.reviews.feitas} de ${results.reviews.total} projetos com revisão devida`} accent={seriesColor(theme, 0)} />
        <StatCard label={`Média da base (chamados em ${base.dias} dias)`} value={numberLabel(base.media, 2)}
          detail={`${base.chamados} chamados de ${base.clientes} clientes ativos fora de implantação`} accent={theme.palette.text.secondary} />
      </div>
      <Panel title="Chamados nos 60 dias após a entrega"
        subtitle="OS do grupo Ticket N1 do mesmo cliente abertas até 60 dias depois da conclusão da etapa 12 Finalização.">
        <TicketsAfterDelivery rows={results.tickets_after_delivery} base={base} />
      </Panel>
      <Typography variant="caption" color="textSecondary">
        Entregue no prazo: data de entrega até a previsão de término da OS pai. Revisão devida: entrega há pelo menos 90 dias, status Concluído ou etapa 13 já concluída.
      </Typography>
    </div>
  );
};

export default ResultsTab;
